import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Calendar, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { EmployeeAvatar, StatusBadge, Panel, ProgressBar, EmptyState } from './components/hr-ui';
import { balanceLabel, formatRange } from './components/leave-format';
import { AccountAccessPanel } from './components/AccountAccessPanel';
import { useDocumentsQuery, useOpenSigned } from '@/client/api/hr/records';
import { useCompHistoryQuery } from '@/client/api/hr/comp';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { BAND_BADGE } from '@/client/types/hr';
import { salary } from './components/comp-format';
import { useHrEmployeeQuery } from '@/client/api/hr/people';
import {
  useEmployees,
  useTimeOffRequests,
  useTimeOffBalances,
  useGoals,
  useOnboardingTasks,
  usePerformanceReviews,
  useReviewCycles,
} from './hr-store';
import {
  CHECKLIST_STATUS_BADGE,
  CHECKLIST_STATUS_LABEL,
  HR_DOCUMENT_KIND_LABEL,
  EMPLOYMENT_STATUS_BADGE,
  EMPLOYMENT_STATUS_LABEL,
  EMPLOYMENT_TYPE_LABEL,
  GOAL_STATUS_BADGE,
  GOAL_STATUS_LABEL,
  ONBOARDING_CATEGORY_LABEL,
  REVIEW_STATUS_BADGE,
  REVIEW_STATUS_LABEL,
  REVIEW_TYPE_BADGE,
  REVIEW_TYPE_LABEL,
  TIME_OFF_STATUS_BADGE,
  TIME_OFF_STATUS_LABEL,
} from '@/client/types/hr';

const EmployeeProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const employees = useEmployees();
  const timeOffRequests = useTimeOffRequests();
  const timeOffBalances = useTimeOffBalances();
  const goals = useGoals();
  const onboardingTasks = useOnboardingTasks();
  const documentsQ = useDocumentsQuery(id);
  const openDoc = useOpenSigned('hr');
  const reviews = usePerformanceReviews();
  const cycles = useReviewCycles();

  const employeeQ = useHrEmployeeQuery(id);
  // The single read carries the linked account; the list row is a fallback while it loads.
  const employee = useMemo(
    () => employeeQ.data ?? employees.find((e) => e.id === id) ?? null,
    [employeeQ.data, employees, id],
  );
  const manager = useMemo(() => employees.find((e) => e.id === employee?.managerId) ?? null, [employees, employee]);
  const reports = useMemo(() => employees.filter((e) => e.managerId === id), [employees, id]);
  const myRequests = useMemo(
    () => timeOffRequests.filter((r) => r.employeeId === id).sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()),
    [timeOffRequests, id],
  );
  const myBalances = useMemo(() => timeOffBalances.filter((b) => b.employeeId === id), [timeOffBalances, id]);
  const myGoals = useMemo(() => goals.filter((g) => g.employeeId === id), [goals, id]);
  const myTasks = useMemo(() => onboardingTasks.filter((t) => t.employeeId === id), [onboardingTasks, id]);
  const myDocuments = documentsQ.data ?? [];
  const myReviews = useMemo(() => reviews.filter((r) => r.employeeId === id), [reviews, id]);
  const cycleName = (cycleId: string) => cycles.find((c) => c.id === cycleId)?.name ?? 'Unknown cycle';

  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Human Resource', href: '/admin/human-resource' },
    { label: 'People' },
    { label: 'Directory', href: '/admin/human-resource/people/directory' },
    { label: employee?.name ?? 'Profile' },
  ]);

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <EmptyState label={employeeQ.isLoading ? 'Loading…' : 'Employee not found.'} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="icon" onClick={() => navigate('/admin/human-resource/people/directory')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <EmployeeAvatar employee={employee} />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{employee.name}</h1>
              <StatusBadge label={EMPLOYMENT_STATUS_LABEL[employee.status]} className={EMPLOYMENT_STATUS_BADGE[employee.status]} />
            </div>
            <p className="text-sm text-gray-500">
              {[employee.title, employee.level, employee.department].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" className="gap-2" onClick={() => navigate('/admin/human-resource/people/directory')}>
          <Pencil className="h-4 w-4" /> Edit in directory
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Details">
          <dl className="space-y-2.5 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Mail className="h-3.5 w-3.5 text-gray-400" /> {employee.email || 'No email on file'}
            </div>
            {employee.location && (
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-3.5 w-3.5 text-gray-400" /> {employee.location}
              </div>
            )}
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="h-3.5 w-3.5 text-gray-400" /> Started {new Date(employee.startDate).toLocaleDateString()}
            </div>
            <div className="pt-1 text-gray-600">Type: {EMPLOYMENT_TYPE_LABEL[employee.employmentType]}</div>
            <div className="text-gray-600">Manager: {manager?.name ?? '—'}</div>
          </dl>
        </Panel>

        <Panel title={`Direct reports (${reports.length})`}>
          {reports.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No direct reports.</p>
          ) : (
            <ul className="space-y-2">
              {reports.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/human-resource/people/directory/${r.id}`)}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-gray-50"
                  >
                    <EmployeeAvatar employee={r} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">{r.name}</p>
                      <p className="truncate text-xs text-gray-400">{r.title}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Time off balances">
          {myBalances.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No balances tracked.</p>
          ) : (
            <div className="space-y-3">
              {myBalances.map((b) => (
                <div key={b.typeId}>
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                    <span>{b.typeName}</span>
                    <span>{balanceLabel(b)}</span>
                  </div>
                  {b.allotted !== null && <ProgressBar value={b.allotted ? ((b.used + b.pending) / b.allotted) * 100 : 0} />}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <AccountAccessPanel employee={employee} />
        <PayPanel employeeId={employee.id} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Recent time off requests">
          {myRequests.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No requests yet.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {myRequests.slice(0, 6).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-gray-800">
                      {r.typeName} · {formatRange(r)} · {r.days}d
                    </p>
                  </div>
                  <StatusBadge label={TIME_OFF_STATUS_LABEL[r.status]} className={TIME_OFF_STATUS_BADGE[r.status]} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Goals">
          {myGoals.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No goals assigned.</p>
          ) : (
            <ul className="space-y-3">
              {myGoals.map((g) => (
                <li key={g.id}>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="truncate text-sm text-gray-800">{g.title}</p>
                    <StatusBadge label={GOAL_STATUS_LABEL[g.status]} className={GOAL_STATUS_BADGE[g.status]} />
                  </div>
                  <ProgressBar value={g.progress} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Onboarding / offboarding tasks">
          {myTasks.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No checklist tasks.</p>
          ) : (
            <ul className="space-y-2">
              {myTasks.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <p className="truncate text-gray-800">{t.title}</p>
                    <p className="text-[11px] text-gray-400">{ONBOARDING_CATEGORY_LABEL[t.category]}</p>
                  </div>
                  <StatusBadge label={CHECKLIST_STATUS_LABEL[t.status]} className={CHECKLIST_STATUS_BADGE[t.status]} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Documents"
          action={
            <Link to="/admin/human-resource/documents/files" className="text-xs font-medium text-blue-700 hover:underline">
              All files
            </Link>
          }
        >
          {myDocuments.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No documents on file.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {myDocuments.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2">
                  <button type="button" className="truncate text-left text-gray-800 hover:text-blue-700" onClick={() => openDoc(`/documents/${d.id}/download`)}>
                    {d.name}
                  </button>
                  <span className="shrink-0 text-[11px] text-gray-400">{HR_DOCUMENT_KIND_LABEL[d.kind]}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-4">
        <Panel title="Performance reviews">
          {myReviews.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">No performance reviews yet.</p>
          ) : (
            <ul className="space-y-3">
              {myReviews.map((r) => {
                const reviewer = employees.find((e) => e.id === r.reviewerId);
                return (
                  <li key={r.id} className="rounded-lg border border-gray-100 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{cycleName(r.cycleId)}</span>
                      <StatusBadge label={REVIEW_TYPE_LABEL[r.type]} className={REVIEW_TYPE_BADGE[r.type]} />
                      <StatusBadge label={REVIEW_STATUS_LABEL[r.status]} className={REVIEW_STATUS_BADGE[r.status]} />
                      <span className="text-xs text-gray-400">by {reviewer?.name ?? 'Unknown'}</span>
                    </div>
                    {r.strengths && <p className="mt-2 text-sm text-gray-700">{r.strengths}</p>}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default EmployeeProfilePage;

/** Pay at a glance — only for `comp.view` holders; `hr.view` alone never sees it. */
function PayPanel({ employeeId }: { employeeId: string }) {
  const allowed = useGate(PERMISSIONS.comp.view).allowed;
  const q = useCompHistoryQuery(employeeId, allowed);
  if (!allowed) return null;
  const h = q.data;
  return (
    <Panel
      title="Pay"
      action={
        <Link to="/admin/human-resource/compensation" className="text-xs font-medium text-blue-700 hover:underline">
          Compensation
        </Link>
      }
    >
      {q.isLoading ? (
        <p className="text-sm text-gray-500">Loading pay…</p>
      ) : q.isError || !h ? (
        <p className="text-sm text-red-600">Could not load pay.</p>
      ) : (
        <div className="space-y-1 text-sm">
          <p className="text-lg font-semibold text-gray-900">{h.current ? salary(h.current) : 'Not recorded'}</p>
          {h.band && <StatusBadge label={BAND_BADGE[h.band].label} className={BAND_BADGE[h.band].className} />}
          {h.scheduled && <p className="text-xs text-gray-500">Scheduled: {salary(h.scheduled)} from {h.scheduled.effectiveOn}</p>}
          <p className="text-xs text-gray-500">
            {h.changes.filter((c) => c.status === 'proposed').length + h.bonuses.filter((b) => b.status === 'proposed').length} waiting
            for approval
          </p>
        </div>
      )}
    </Panel>
  );
}
